import { appConfig, EventName } from "@utils/analytics";
import { JsonMap, useAnalytics } from "@segment/analytics-react-native";
import { store } from "@redux/store";
import { Dimensions, Platform } from "react-native";
import { getContext } from "@utils/web-properties";
import { preLoginRoutes } from "@utils/product/productValidators";
import { useSegmentClient } from "../contexts/SegmentClientContext";
import { parseUserAgent } from "@scenes/authentication/utils/utils";
import * as Device from "expo-device";
import { getDeviceType } from "@components/molecules/AdView/BannerAd/utils";
import { DeviceType } from "@utils/constants";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const getSystemTypeEnum = (deviceType: any) => {
  let type = DeviceType.WEB;
  if (deviceType) {
    switch (deviceType) {
      case "Linux":
        type = DeviceType.ANDROID;
        break;
      case "iPhone":
        type = DeviceType.IOS;
        break;
      default:
        type = DeviceType.WEB;
        break;
    }
  } else {
    type = Platform.OS;
  }
  return getDeviceType(type).toString();
};

export const formatOSName = (rawOsName: string) => {
  if (!rawOsName) return "";

  const osName = rawOsName.toLowerCase();

  const osMap = {
    android: "Android",
    ios: "iOS",
    ipad: "iPadOS",
    ipados: "iPadOS",
    windows: "Windows",
    macos: "macOS",
    mac: "macOS",
    linux: "Linux",
    web: "Web",
    tizen: "Tizen",
    harmonyos: "HarmonyOS",
    fireos: "Fire OS",
  };

  for (const key in osMap) {
    if (osName.includes(key)) {
      return osMap[key];
    }
  }

  return rawOsName.charAt(0).toUpperCase() + rawOsName.slice(1);
};

export const getUserAnalyticsPayload = () => {
  const state = store.getState();

  const user = state.userInfo.user;
  const settings = state?.orgSettings;
  const isVDBOrg = settings?.organization?.id === 1;
  const deviceDetails =
    Platform.OS === "web"
      ? {
          ...parseUserAgent(navigator.userAgent),
          system_type_enum: getSystemTypeEnum(null),
          platform_action_enum: "0",
        }
      : {
          deviceName: Device.deviceName,
          osName: formatOSName(Platform.OS),
          system_type_enum: getSystemTypeEnum(Device.deviceType),
          platform_action_enum: "1",
        };
  let context: any = {};
  const { height } = Dimensions.get("window");
  const { deviceWidth: width } = useIsFoldable();
  if (Platform.OS === "web") {
    context = getContext();
  }
  return {
    ...appConfig,
    user_company_id: user?.company_id?.toString(),
    user_company_name: user?.company,
    user_email: user?.email,
    user_id: user?.id?.toString(),
    user_name: user?.first_name
      ? `${user?.first_name} ${user?.last_name}`
      : undefined,
    user_organization_id: settings?.organization?.id?.toString(),
    user_organization_name: settings?.organization?.name,
    user_price_mode: user?.price_mode?.toString(),
    price_mode: user?.price_mode?.toString(),
    ...(isVDBOrg && {
      paid_plan_user: user?.paid_plan_user?.toString(),
      is_admin_true: user?.is_admin_true?.toString(),
    }),
    has_plan_details: user?.plan_details ? true : false,
    ...(Platform.OS === "web" && {
      path: context.page.path || "",
      url: context.page.url || "",
      userAgent: context.userAgent,
      locale: context.locale,
      context: {
        platform: Platform.OS,
      },
      brand: context.browserInfo.browser,
      version: context.browserInfo.version,
      platform: context.browserInfo.platform,
      mobile: context.browserInfo.mobile,
      timezone: context.timezone,
      deviceResolution: `${width}x${height}`,
    }),
    ...deviceDetails,
  };
};

export const isSegmentUserAgent = (userAgent: string) => {
  if (!userAgent) return false;

  // Regex to match Segment's specific Safari-like pattern
  const segmentPattern =
    /^Mozilla\/5\.0 \([^;]+; CPU [^)]+ like Mac OS X\) AppleWebKit\/600\.1\.4 \(KHTML, like Gecko\) Version\/\d\.0 Mobile\/10B329 Safari\/8536\.25$/;

  return segmentPattern.test(userAgent);
};

export function isBot(userAgent = "") {
  if (!userAgent && Platform.OS === "web") return true;

  const botPatterns = [
    /bot/i, // "Googlebot", "Bingbot"
    /crawl/i, // "crawler"
    /spider/i, // "spider"
    /HeadlessChrome/i, // Puppeteer/Headless
    /PhantomJS/i, // Old automation
    /slurp/i, // Yahoo Slurp bot
    /curl/i, // CLI traffic
    /wget/i, // CLI downloads
    /httpclient/i, // Generic clients
  ];

  return botPatterns.some((pattern) => pattern.test(userAgent));
}

export const useUserAnalytics = () => {
  const state = store.getState();
  const user = state.userInfo.user;
  const segmentClient = useSegmentClient();
  const {
    track: segmentTrack,
    identify: segmentIdentify,
    screen: segmentScreen,
  } = useAnalytics();

  const track: typeof segmentTrack = async (...args) =>
    segmentClient ? segmentTrack(...args) : undefined;
  const identify: typeof segmentIdentify = async (...args) =>
    segmentClient ? segmentIdentify(...args) : undefined;
  const screen: typeof segmentScreen = async (...args) =>
    segmentClient ? segmentScreen(...args) : undefined;

  const segmentUserInfo = segmentClient?.userInfo?.get();

  const _track = async (eventName: string, options: any) => {
    if (!segmentClient) return;
    const userAnalyticsPayload = getUserAnalyticsPayload();
    if (isSegmentUserAgent(userAnalyticsPayload?.userAgent)) {
      console.warn(
        "Skipping tracking for Segment user agent:",
        userAnalyticsPayload.userAgent
      );
      return;
    }
    if (isBot(userAnalyticsPayload?.userAgent)) {
      console.warn(
        "Skipping tracking for bot user agent:",
        userAnalyticsPayload.userAgent
      );
      return;
    }
    if (!segmentUserInfo?.userId || !user?.id) {
      console.error(
        "Track Event tracked without user:",
        eventName,
        JSON.stringify(options)
      );
      if (user?.id) {
        await identify(`${user?.id}`, {
          name: `${user?.first_name} ${user?.last_name}`,
          email: user?.email,
          country: user?.country,
          ...appConfig,
        });
      } else {
        console.error(
          "Can't identify as user is not logged in",
          JSON.stringify(user)
        );
      }
    }
    await track(eventName, {
      ...userAnalyticsPayload,
      ...options,
    });
  };

  const _identify = async (userId?: string, userTraits?: JsonMap) => {
    await identify(userId, userTraits);
  };

  const _screen = async (name: string, options?: JsonMap) => {
    if (!segmentClient) return;
    const userAnalyticsPayload = getUserAnalyticsPayload();

    if (!preLoginRoutes.includes(name)) {
      if (!segmentUserInfo?.userId || !user?.id) {
        console.error(
          "Screen Event tracked without user:",
          name,
          JSON.stringify(options)
        );
        if (user?.id) {
          await identify(`${user?.id}`, {
            name: `${user?.first_name} ${user?.last_name}`,
            email: user?.email,
            country: user?.country,
            ...appConfig,
          });
        } else {
          console.error(
            "Can't identify as user is not logged in",
            JSON.stringify(user)
          );
        }
      }
      await screen(name, {
        ...userAnalyticsPayload,
        ...options,
      });
    }
  };

  const trackEvent = (eventName: string, options: any) => {
    const userAnalyticsPayload = getUserAnalyticsPayload();
    track(eventName, {
      ...userAnalyticsPayload,
      ...options,
    }).then();
  };

  const trackButtonEvent = (
    eventName: string,
    eventType: string,
    options: any,
    viewType?: string
  ) => {
    if (!segmentClient) {
      return;
    }
    const userAnalyticsPayload = getUserAnalyticsPayload();
    track(eventName, {
      ...(eventType && { button_name: eventType }),
      ...(viewType && { view_type: viewType || "" }),
      ...userAnalyticsPayload,
      ...options,
    }).then();
  };

  const trackHyperLinkEvent = (
    link: string,
    viewType?: string,
    options?: any
  ) => {
    const userAnalyticsPayload = getUserAnalyticsPayload();
    track(EventName.HYPER_LINK_CLICK, {
      hyper_link: link,
      ...(viewType && { view_type: viewType || "" }),
      ...userAnalyticsPayload,
      ...options,
    }).then();
  };

  const trackWebEvent = (eventName: string, options: any) => {
    _track(eventName, {
      ...options,
    }).then();
  };

  const trackAdEvent = (eventName: string, options: any) => {
    _track(eventName, {
      ...options,
    }).then();
  };

  return {
    trackEvent,
    trackButtonEvent,
    trackWebEvent,
    trackAdEvent,
    trackHyperLinkEvent,
    _identify,
    _screen,
    _track,
  };
};
